import * as React from 'react';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import {
    AimOutlined,
    CaretDownOutlined,
    CheckCircleFilled,
    CheckCircleOutlined,
    ClockCircleFilled,
    ClockCircleOutlined,
    CloseCircleFilled,
    CloseCircleOutlined,
    InboxOutlined,
    LoadingOutlined,
    PicRightOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import {
    FixRequestStatusType,
    IssueEntity,
    IssueFixRequest,
    IssueState,
    OrgUser,
    ReleaseStatus,
    ReleaseStepType,
} from '@/types';
import { antGrey, getAvatarColorForName, runwayColors, rwColors } from '@/components/styles/globalStyles';
import { RootState } from '@/reducers/reducers';
import {
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectIsTriageHiddenForApp,
    selectRelease,
    selectReleasesForAppId,
    selectRequiredFixRequestsApprovals,
    selectRequiresFixRequestsApprovals,
    selectUserById,
} from '@/selectors/selectors';
import { GetFixRequestAuthorIds, GetFixRequestCurrentApprovals, getFixRequestFormFieldsSummary } from '@/helpers';
import { routeForReleaseStepOrNav } from '@/helpers/routeHelpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import { formatDateWithTimezone } from '@/timeHelpers';
import { displayUserNameLong } from '@/helpers/displayHelpers';
import {
    TRY_TRANSITION_ISSUE,
    TRY_UPDATE_ISSUE_FIX_REQUEST,
    TriageViewType,
    tryTransitionIssue,
    tryUpdateIssueFixRequest,
} from '@/actions/triage';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useIsLoading, useIsRequiredFixApprover } from '@/utils/hooks/helpers';
import type { FeatureReadinessFixContext } from './FixRequestSection';
import { CLOSED_ISSUE_STATES, usePendingIssueTransition } from './triageHelpers';
import { RequestFixModal } from './RequestFixModal';
import routes from '../../routes';
import JiraOutlinedIcon from '@/components/icons/JiraOutlinedIcon';

export const LABEL_STYLE: React.CSSProperties = {
    color: antGrey.grey8,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
};

export const FIX_VERSION_CODE_LOCK_TOOLTIP =
    'Changing target version is not possible due to related code changes already pending or merged.';

const CARD_COL = { status: 200, reporter: 100, assignee: 100, version: 160, actions: 56 };
const FEATURE_READINESS_STATUS_COL = 305;
export const cardGridColumns = (hasReporter: boolean, preview?: boolean, statusColWidth?: number) => {
    const cols = [`${statusColWidth ?? CARD_COL.status}px`, 'minmax(0, 1fr)'];
    if (hasReporter && !preview) cols.push(`${CARD_COL.reporter}px`);
    cols.push(`${CARD_COL.assignee}px`);
    if (!preview) cols.push(`${CARD_COL.version}px`, `${CARD_COL.actions}px`);
    return cols.join(' ');
};

export const UserAvatarWithTooltip: React.FC<{
    user?: OrgUser;
    fallbackName?: string;
    size?: number;
    extraTooltip?: string;
    bordered?: boolean;
}> = ({ user, fallbackName, size = 28, extraTooltip, bordered }) => {
    const name = user ? displayUserNameLong(user) : (fallbackName ?? '');
    const bgColor = getAvatarColorForName(name);
    const tooltip =
        extraTooltip ?
            <>
                {name ?
                    <>
                        {name}
                        <br />
                    </>
                :   null}
                {extraTooltip}
            </>
        :   name || undefined;
    const avatar = (
        <Avatar
            size={size}
            src={user?.commsAvatars?.image32}
            style={{ backgroundColor: bgColor, flexShrink: 0, fontSize: 12, border: 'none' }}
        >
            {name.charAt(0).toUpperCase()}
        </Avatar>
    );
    return (
        <Tooltip title={tooltip} placement="top">
            {bordered ?
                <span
                    style={{
                        display: 'inline-flex',
                        borderRadius: '50%',
                        padding: 2,
                        background: `repeating-conic-gradient(${bgColor} 0deg 6deg, transparent 6deg 15deg)`,
                    }}
                >
                    {avatar}
                </span>
            :   avatar}
        </Tooltip>
    );
};

interface FixRequestStatusMeta {
    label: string;
    icon: React.ReactNode;
    pending: boolean;
}

export const fixRequestStatusMeta = (state: IssueState, requiresFixApprovals = true): FixRequestStatusMeta => {
    if (state === IssueState.FixRequested && !requiresFixApprovals) {
        return {
            label: 'Fix in progress',
            icon: <ClockCircleFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            pending: false,
        };
    }
    switch (state) {
        case IssueState.FixApproved:
            return {
                label: 'Fix approved',
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixMerged:
            return {
                label: 'Fix merged',
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixRejected:
            return {
                label: 'Fix rejected',
                icon: <CloseCircleFilled style={{ color: rwColors.statusRed, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixRequested:
        default:
            return {
                label: 'Pending approval',
                icon: <ClockCircleFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
                pending: state === IssueState.FixRequested,
            };
    }
};

const CANCELED_FIX_STATUS_META: FixRequestStatusMeta = {
    label: 'Fix canceled',
    icon: <CloseCircleFilled style={{ color: antGrey.grey7, fontSize: 16 }} />,
    pending: false,
};

export const FixRequestModule: React.FC<{
    orgId: string;
    issue?: IssueEntity;
    featureReadiness?: FeatureReadinessFixContext;
    dimmed?: boolean;
    disabled?: boolean;
    canceled?: boolean;
    viewType?: TriageViewType;
}> = ({ issue, orgId, featureReadiness, dimmed, disabled, canceled, viewType }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const fixRequest: IssueFixRequest | undefined = issue ? issue.fixRequest : featureReadiness?.fixRequest;
    const appId = issue?.appId ?? featureReadiness?.appId ?? '';
    const displayState: IssueState =
        issue ? issue.state
        : fixRequest?.status?.status === FixRequestStatusType.FixRequestApproved ? IssueState.FixApproved
        : fixRequest?.status?.status === FixRequestStatusType.FixRequestRejected ? IssueState.FixRejected
        : IssueState.FixRequested;
    const isTriageHidden = useSelector((state: RootState) => selectIsTriageHiddenForApp(state, appId));
    // A canceled fix is settled: its approval state is moot, so it reads as canceled rather than pending approval.
    const appRequiresFixApprovals = useSelector(selectRequiresFixRequestsApprovals(appId));
    const appLevelApprovalsEnabled =
        featureReadiness ? featureReadiness.requiresFixRequestsApprovals : appRequiresFixApprovals;
    const hasTerminalStatus =
        displayState === IssueState.FixApproved ||
        displayState === IssueState.FixRejected ||
        displayState === IssueState.FixMerged;
    const fixHasApprovalRequirement =
        fixRequest?.fixApprovalSettings ? fixRequest.fixApprovalSettings.requiresApprovals : appLevelApprovalsEnabled;
    const meta = canceled ? CANCELED_FIX_STATUS_META : fixRequestStatusMeta(displayState);
    const isTargetVersionOnly = !!issue && !fixRequest && !!issue.targetVersion;
    const showApprovalControls = !isTargetVersionOnly && (fixHasApprovalRequirement || hasTerminalStatus || canceled);
    const barBackground = isTargetVersionOnly ? runwayColors.white : antGrey.grey3;
    const [requestFixOpen, setRequestFixOpen] = React.useState(false);
    const requesterId = fixRequest?.createdBy;
    const requester = useSelector((state: RootState) =>
        requesterId ? selectUserById(state, orgId, appId, requesterId) : undefined,
    );
    const status = fixRequest?.status;
    const approverIds = status ? GetFixRequestAuthorIds(status) : [];
    const deciderId = approverIds.length > 0 ? approverIds[approverIds.length - 1] : undefined;
    const decider = useSelector((state: RootState) =>
        deciderId ? selectUserById(state, orgId, appId, deciderId) : undefined,
    );
    const isRejected = displayState === IssueState.FixRejected;
    const showDecided =
        !!deciderId && (isRejected || displayState === IssueState.FixApproved || displayState === IssueState.FixMerged);
    const requiredApprovals = useSelector(selectRequiredFixRequestsApprovals(appId)) || 1;
    const currentApprovals = status ? GetFixRequestCurrentApprovals(status) : 0;
    const approverUsers = useSelector(
        (state: RootState) => approverIds.map((id) => selectUserById(state, orgId, appId, id)),
        shallowEqual,
    );
    const showApprovalCount = requiredApprovals > 1;
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const decidedAtLabel =
        showDecided && status?.updatedAt ?
            formatDateWithTimezone(status.updatedAt, 'longMonthDateYearTime', effectiveTimezone, use24HourTimeFormat)
        :   undefined;
    const isTransitioningIssue = useIsLoading(TRY_TRANSITION_ISSUE, issue?.id);
    const approveIsLoading = useIsLoading(ReleaseStepUpdatable.ApproveFixRequest, featureReadiness?.fixRequest.id);
    const rejectIsLoading = useIsLoading(ReleaseStepUpdatable.RejectFixRequest, featureReadiness?.fixRequest.id);
    const isTransitioning = issue ? isTransitioningIssue : approveIsLoading || rejectIsLoading;
    const { pendingTransition, setPendingTransition } = usePendingIssueTransition(isTransitioningIssue);
    const isTransitioningTo = (targetState: IssueState) =>
        issue ? isTransitioningIssue && pendingTransition === targetState
        : targetState === IssueState.FixApproved ? approveIsLoading
        : rejectIsLoading;
    const canApproveRejectRole = useUserCanPerformAction(
        RunwayUserAction.UAUpdateFixRequestStatus,
        undefined,
        undefined,
        undefined,
        undefined,
        appId,
    );
    const isRequiredApprover = useIsRequiredFixApprover(orgId, appId);
    const canApproveReject = canApproveRejectRole && isRequiredApprover;
    const canManage = useUserCanPerformAction(
        RunwayUserAction.UAManageTriageIssues,
        undefined,
        undefined,
        undefined,
        undefined,
        appId,
    );
    // A closed issue can't move to fix requested, so only the link goes: the target version still shows.
    const canCreateFixRequest =
        isTargetVersionOnly && canManage && !disabled && !CLOSED_ISSUE_STATES.includes(displayState);
    const createFixRequestLink = (
        <a
            onClick={(e) => {
                e.stopPropagation();
                setRequestFixOpen(true);
            }}
            style={{ fontSize: 14, whiteSpace: 'nowrap' }}
        >
            {appRequiresFixApprovals ? 'Create fix request' : 'Create fix'}
        </a>
    );
    const canEditReleaseFixForUser = useUserCanPerformAction(
        undefined,
        featureReadiness?.releaseId,
        undefined,
        undefined,
        undefined,
        appId,
    );
    const canEditReleaseFix = featureReadiness ? canEditReleaseFixForUser : false;
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, appId));
    const releaseVersion = useSelector((state: RootState) =>
        featureReadiness ? selectRelease(state, featureReadiness.releaseId)?.version : undefined,
    );
    const isUpdatingIssueFixRequest = useIsLoading(TRY_UPDATE_ISSUE_FIX_REQUEST, issue?.id);
    const updateFixIsLoading = useIsLoading(ReleaseStepUpdatable.UpdateFixRequest, featureReadiness?.fixRequest.id);
    const isUpdatingFixRequest = isUpdatingIssueFixRequest || updateFixIsLoading;
    const fixVersionOptions = releases
        .filter(
            (release) =>
                ![ReleaseStatus.Completed, ReleaseStatus.Skipped, ReleaseStatus.Deleted].includes(release.status),
        )
        .map((release) => ({ value: release.version, label: release.displayableVersion || release.version }));
    const codeChangesLockVersion = !!fixRequest?.hasCodeChangesPendingOrMerged;
    const canEditVersionPermission = issue ? canManage : canEditReleaseFix;
    const canEditFixVersion =
        canEditVersionPermission &&
        !disabled &&
        !isTargetVersionOnly &&
        displayState !== IssueState.FixMerged &&
        !codeChangesLockVersion;
    const fixVersionTooltip =
        codeChangesLockVersion ? FIX_VERSION_CODE_LOCK_TOOLTIP
        : !canEditVersionPermission ? DISALLOWED_ACTION_ERROR_MESSAGE
        : undefined;
    const currentVersion = issue ? issue.targetVersion : releaseVersion;
    const isApprovedLike = displayState === IssueState.FixApproved || displayState === IssueState.FixMerged;
    const showApproveReject =
        !isTargetVersionOnly && ((fixHasApprovalRequirement && meta.pending) || isRejected || isApprovedLike);
    const canTransition = meta.pending && canApproveReject && !disabled;
    const transition = (targetState: IssueState) => {
        setPendingTransition(targetState);
        if (issue) {
            dispatch(tryTransitionIssue({ orgId, appId, issueId: issue.id, targetState }));
            return;
        }
        if (!featureReadiness) {
            return;
        }
        dispatch(
            tryUpdateReleaseStep(
                targetState === IssueState.FixApproved ?
                    ReleaseStepUpdatable.ApproveFixRequest
                :   ReleaseStepUpdatable.RejectFixRequest,
                {
                    appId: featureReadiness.appId,
                    orgId,
                    releaseId: featureReadiness.releaseId,
                    stepRunId: featureReadiness.stepRunId,
                    frItemId: featureReadiness.featureItemId,
                },
                { fixId: featureReadiness.fixRequest.id, isAutoIgnoreItem: featureReadiness.isAutoIgnoreItem },
                featureReadiness.fixRequest.id,
            ),
        );
    };
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    const hasReporter = !!issue?.reporter;
    const fixRequestSummary = getFixRequestFormFieldsSummary(fixRequest?.formFieldsValues);
    const goToFeatureReadiness = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!currentVersion) {
            return;
        }
        const base = routeForReleaseStepOrNav(orgId, appId, currentVersion, ReleaseStepType.FeatureReadiness);
        // Deep-link the fix's item so Feature Readiness opens it directly (workItems on the full issue, workItemIds
        // on the lighter list payload). Falls back to the step summary when neither is available.
        const itemId =
            fixRequest ? (fixRequest.workItems?.[0]?.id ?? fixRequest.workItemIds?.[0]) : issue?.ticketFeatureItemId;
        history.push(itemId ? `${base}?${new URLSearchParams({ selectedItem: itemId }).toString()}` : base);
    };
    const goToTriageIssue = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!fixRequest?.triageIssueId) {
            return;
        }
        history.push(
            generatePath(routes.appTriageIssue, {
                orgIdParam: orgId,
                appIdParam: appId,
                issueIdParam: fixRequest.triageIssueId,
            }),
        );
    };

    const approverAvatars = (
        <div style={{ display: 'flex' }}>
            {approverIds.map((id, idx) => (
                <div
                    key={id}
                    style={{
                        marginLeft: idx === 0 ? 0 : -8,
                        border: `2px solid ${antGrey.grey3}`,
                        borderRadius: '50%',
                        display: 'inline-flex',
                    }}
                >
                    <UserAvatarWithTooltip user={approverUsers[idx]} fallbackName={id} size={20} />
                </div>
            ))}
        </div>
    );

    // Same column tracks as the body (cardGridColumns) so status / content / version / actions line up by
    // construction — no pixel offsets. Each cell just fills its track.
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: cardGridColumns(
                    hasReporter,
                    false,
                    featureReadiness ? FEATURE_READINESS_STATUS_COL : undefined,
                ),
                alignItems: 'center',
                backgroundColor: barBackground,
                borderBottom: `1px ${isTargetVersionOnly ? 'dashed' : 'solid'} ${antGrey.grey5}`,
                cursor: issue ? 'pointer' : 'default',
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            {/* status: fix-request status label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, padding: '12px 8px 12px 16px' }}>
                {showApprovalControls ?
                    <>
                        {meta.icon}
                        <Text style={LABEL_STYLE}>{meta.label}</Text>
                    </>
                : isTargetVersionOnly ?
                    <>
                        <JiraOutlinedIcon fillColor={barBackground} style={{ color: antGrey.grey7 }} />
                        <Text style={{ ...LABEL_STYLE, color: antGrey.grey7 }}>Fix targeted</Text>
                    </>
                :   <>
                        <ToolOutlined style={{ color: antGrey.grey7, fontSize: 16 }} />
                        <Text style={LABEL_STYLE}>Fix</Text>
                    </>
                }
            </div>

            {/* content: feature-readiness jump link + requester + summary. The link sits in a 24px slot (like the
                integration icon below) so it centers on that icon and the requester/summary start at the title. */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    minWidth: 0,
                    padding: '12px 16px',
                }}
            >
                <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {issue && (fixRequest?.fixVersion || (isTargetVersionOnly && issue.ticketFeatureItemId)) && (
                        <Tooltip title="View feature readiness" placement="top">
                            <PicRightOutlined
                                onClick={goToFeatureReadiness}
                                style={{ color: rwColors.primary, fontSize: 16, cursor: 'pointer' }}
                            />
                        </Tooltip>
                    )}
                    {featureReadiness && fixRequest?.triageIssueId && !isTriageHidden && (
                        <Tooltip title="View issue in Triage" placement="top">
                            <InboxOutlined
                                onClick={goToTriageIssue}
                                style={{ color: rwColors.primary, fontSize: 16, cursor: 'pointer' }}
                            />
                        </Tooltip>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {requesterId && <UserAvatarWithTooltip user={requester} fallbackName={requesterId} size={20} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {fixRequestSummary && (
                            <Text ellipsis style={{ color: antGrey.grey8, fontSize: 13 }}>
                                {fixRequestSummary}
                            </Text>
                        )}
                        {isTargetVersionOnly && (
                            <Text ellipsis style={{ color: antGrey.grey6, fontSize: 13 }}>
                                Ticket has fix version applied
                                {canCreateFixRequest && (
                                    <>
                                        {' · '}
                                        {createFixRequestLink}
                                    </>
                                )}
                            </Text>
                        )}
                    </div>
                </div>
            </div>

            {/* reporter column placeholder — keeps alignment when the body shows a reporter */}
            {hasReporter && <div />}

            {/* assignee: approval status + count + approvers */}
            <div
                onClick={stop}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 12,
                    minWidth: 0,
                    padding: '12px 8px',
                }}
            >
                {showApprovalControls && meta.pending && showApprovalCount && (
                    <>
                        <ClockCircleOutlined style={{ color: rwColors.statusYellow, fontSize: 13 }} />
                        <Text style={{ color: antGrey.grey8, fontSize: 14, whiteSpace: 'nowrap' }}>
                            {currentApprovals} of {requiredApprovals}
                        </Text>
                        {approverIds.length > 0 && approverAvatars}
                        <span
                            style={{
                                width: 1,
                                height: 20,
                                backgroundColor: antGrey.grey5,
                                flexShrink: 0,
                                // Even ~20px air on both sides: +8 completes the 12 gap to the count (=20), and -4
                                // pulls the bar near the cell edge so the gap to the aim is also ~20.
                                marginLeft: 8,
                                marginRight: -4,
                            }}
                        />
                    </>
                )}
                {showApprovalControls && showDecided && (
                    <>
                        {isRejected ?
                            <CloseCircleOutlined style={{ color: rwColors.statusRed, fontSize: 16 }} />
                        :   <CheckCircleOutlined style={{ color: rwColors.statusGreen, fontSize: 16 }} />}
                        {!isRejected && showApprovalCount && (
                            <Text style={{ color: antGrey.grey8, fontSize: 12, whiteSpace: 'nowrap' }}>
                                {currentApprovals} of {requiredApprovals}
                            </Text>
                        )}
                        {!isRejected && showApprovalCount && approverIds.length > 0 ?
                            approverAvatars
                        :   <UserAvatarWithTooltip
                                user={decider}
                                fallbackName={deciderId}
                                size={20}
                                extraTooltip={decidedAtLabel}
                            />
                        }
                    </>
                )}
            </div>

            <div
                onClick={stop}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '12px 0 12px 16px',
                }}
            >
                {currentVersion && (
                    <div style={{ position: 'relative', width: 104 }}>
                        <div
                            style={{
                                position: 'absolute',
                                left: -20,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 7,
                            }}
                        >
                            <Tooltip title="Target fix version" placement="top">
                                <AimOutlined style={{ color: antGrey.grey8, fontSize: 13 }} />
                            </Tooltip>
                        </div>
                        <Tooltip title={fixVersionTooltip}>
                            <Select
                                size="small"
                                disabled={isUpdatingFixRequest || !canEditFixVersion}
                                value={currentVersion ?? undefined}
                                options={fixVersionOptions}
                                showSearch
                                optionFilterProp="label"
                                style={{ width: 106 }}
                                popupMatchSelectWidth={false}
                                labelRender={({ label, value }) => (
                                    <span style={{ fontWeight: 600, fontSize: 12, color: antGrey.grey8 }}>
                                        {label ?? value}
                                    </span>
                                )}
                                suffixIcon={
                                    isUpdatingFixRequest ?
                                        <LoadingOutlined spin style={{ color: antGrey.grey6 }} />
                                    :   <CaretDownOutlined
                                            style={{ color: antGrey.grey6, fontSize: 16, pointerEvents: 'none' }}
                                        />
                                }
                                onChange={(value) => {
                                    if (issue) {
                                        dispatch(
                                            tryUpdateIssueFixRequest({
                                                orgId,
                                                appId,
                                                issueId: issue.id,
                                                version: value,
                                            }),
                                        );
                                        return;
                                    }
                                    if (!featureReadiness) {
                                        return;
                                    }
                                    dispatch(
                                        tryUpdateReleaseStep(
                                            ReleaseStepUpdatable.UpdateFixRequest,
                                            {
                                                appId: featureReadiness.appId,
                                                orgId,
                                                releaseId: featureReadiness.releaseId,
                                                stepRunId: featureReadiness.stepRunId,
                                                frItemId: featureReadiness.featureItemId,
                                            },
                                            {
                                                fixId: featureReadiness.fixRequest.id,
                                                version: value,
                                            },
                                            featureReadiness.fixRequest.id,
                                        ),
                                    );
                                }}
                            />
                        </Tooltip>
                    </div>
                )}
            </div>

            {/* actions: approve / reject */}
            <div
                onClick={stop}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 8,
                    padding: '12px 10px 12px 0',
                }}
            >
                {showApproveReject && (
                    <>
                        <Tooltip
                            title={
                                !meta.pending ? undefined
                                : canApproveReject ?
                                    'Approve fix'
                                :   DISALLOWED_ACTION_ERROR_MESSAGE
                            }
                            placement="top"
                        >
                            <Button
                                size="small"
                                shape="circle"
                                loading={isTransitioningTo(IssueState.FixApproved)}
                                disabled={!canTransition || isTransitioning}
                                onClick={() => transition(IssueState.FixApproved)}
                                icon={
                                    isTransitioningTo(IssueState.FixApproved) ? undefined : (
                                        <CheckCircleOutlined
                                            style={{
                                                color: isApprovedLike ? rwColors.statusGreen : antGrey.grey6,
                                                fontSize: 20,
                                            }}
                                        />
                                    )
                                }
                                style={{
                                    width: 20,
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: runwayColors.white,
                                    boxShadow: `0 0 0 2px ${runwayColors.white}`,
                                    opacity: canTransition ? undefined : 0.4,
                                }}
                            />
                        </Tooltip>
                        <Tooltip
                            title={
                                !meta.pending ? undefined
                                : canApproveReject ?
                                    'Reject fix'
                                :   DISALLOWED_ACTION_ERROR_MESSAGE
                            }
                            placement="top"
                        >
                            <Button
                                size="small"
                                type="text"
                                shape="circle"
                                loading={isTransitioningTo(IssueState.FixRejected)}
                                disabled={!canTransition || isTransitioning}
                                onClick={() => transition(IssueState.FixRejected)}
                                icon={
                                    isTransitioningTo(IssueState.FixRejected) ? undefined : (
                                        <CloseCircleOutlined
                                            style={{
                                                color: isRejected ? rwColors.statusRed : antGrey.grey6,
                                                fontSize: 20,
                                            }}
                                        />
                                    )
                                }
                                style={{
                                    width: 20,
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: runwayColors.white,
                                    boxShadow: `0 0 0 2px ${runwayColors.white}`,
                                    opacity: canTransition ? undefined : 0.4,
                                }}
                            />
                        </Tooltip>
                    </>
                )}
            </div>
            {isTargetVersionOnly && viewType && (
                <div onClick={stop}>
                    <RequestFixModal
                        open={requestFixOpen}
                        onClose={() => setRequestFixOpen(false)}
                        orgId={orgId}
                        appId={appId}
                        issue={issue}
                        viewType={viewType}
                    />
                </div>
            )}
        </div>
    );
};
